/**
 * Markup for the Report Result modal. Pure HTML-string builder, separated from
 * the component's behaviour (validation, summary, submit) so each concern reads
 * on its own. Inputs are the resolved team names + any pre-filled set scores.
 */

export interface ReportFormScores {
  s1t1: string | number;
  s1t2: string | number;
  s2t1: string | number;
  s2t2: string | number;
  s3t1: string | number;
  s3t2: string | number;
}

export interface ReportFormOptions extends ReportFormScores {
  matchId: number;
  team1Name: string;
  team2Name: string;
}

export const renderReportForm = (o: ReportFormOptions): string => `
      <div id="report-modal-overlay" class="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
        <div class="relative w-full max-w-4xl bg-white dark:bg-card-dark daylight:bg-day-raised rounded-2xl shadow-2xl daylight:shadow-day-modal border border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 overflow-hidden animate-in zoom-in-95 duration-200">

          <!-- Header -->
          <div class="px-8 pt-8 pb-4 border-b border-slate-200 dark:border-slate-800 daylight:border-day-ink/10">
            <div class="flex justify-between items-start">
              <div>
                <h2 class="text-3xl font-black text-slate-900 dark:text-white daylight:text-day-ink">Report Match Result</h2>
                <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft mt-1 text-sm">
                  Court ${o.matchId} • Best of 3 Sets
                </p>
              </div>
              <button type="button" id="close-modal-btn" class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 daylight:text-day-ink-muted daylight:hover:text-day-ink transition-colors p-2">
                <span class="material-symbols-outlined">close</span>
              </button>
            </div>
          </div>

          <!-- Form Content -->
          <div class="px-8 py-6">
            <form id="result-form" class="space-y-6">

              <!-- Score Input Grid (Stitch Style) -->
              <div>
                <!-- Column Headers -->
                <div class="grid grid-cols-12 gap-4 mb-4">
                  <div class="col-span-6"></div>
                  <div class="col-span-2 text-center">
                    <span class="text-xs font-black text-slate-400 daylight:text-day-ink-muted uppercase tracking-widest">Set 1</span>
                  </div>
                  <div class="col-span-2 text-center">
                    <span class="text-xs font-black text-slate-400 daylight:text-day-ink-muted uppercase tracking-widest">Set 2</span>
                  </div>
                  <div class="col-span-2 text-center">
                    <span class="text-xs font-black text-slate-400 daylight:text-day-ink-muted uppercase tracking-widest opacity-40">Set 3</span>
                  </div>
                </div>

                <!-- Team A Row -->
                <div class="grid grid-cols-12 gap-4 items-center mb-1 group">
                  <div class="col-span-6 flex items-center gap-3">
                    <div class="w-1.5 h-10 bg-primary rounded-full flex-shrink-0"></div>
                    <div>
                      <p class="text-[10px] font-bold text-primary daylight:text-day-ink-muted uppercase tracking-wider">Team A</p>
                      <p class="font-bold text-lg text-slate-900 dark:text-white">${o.team1Name}</p>
                    </div>
                  </div>

                  <!-- Set 1 -->
                  <div class="col-span-2">
                    <input
                      type="number"
                      class="w-full h-14 bg-slate-100 dark:bg-slate-800/50 daylight:bg-day-sunken daylight:text-day-ink border-2 border-transparent focus:border-primary focus:ring-0 rounded-xl text-center text-2xl font-black transition-all set-input team1-set1"
                      name="set-1-team1"
                      value="${o.s1t1}"
                      placeholder="0"
                      min="0"
                      max="25"
                      required
                      tabindex="1"
                    />
                  </div>

                  <!-- Set 2 -->
                  <div class="col-span-2">
                    <input
                      type="number"
                      class="w-full h-14 bg-slate-100 dark:bg-slate-800/50 daylight:bg-day-sunken daylight:text-day-ink border-2 border-transparent focus:border-primary focus:ring-0 rounded-xl text-center text-2xl font-black transition-all set-input team1-set2"
                      name="set-2-team1"
                      value="${o.s2t1}"
                      placeholder="0"
                      min="0"
                      max="25"
                      required
                      tabindex="3"
                    />
                  </div>

                  <!-- Set 3 -->
                  <div id="set-3-wrapper-team1" class="col-span-2 opacity-40 transition-opacity">
                    <input
                      type="number"
                      class="w-full h-14 bg-slate-100 dark:bg-slate-800/50 daylight:bg-day-sunken daylight:text-day-ink border-2 border-transparent focus:border-primary focus:ring-0 rounded-xl text-center text-2xl font-black transition-all set-input team1-set3"
                      name="set-3-team1"
                      value="${o.s3t1}"
                      placeholder="0"
                      min="0"
                      max="25"
                      tabindex="5"
                      disabled
                    />
                  </div>
                </div>

                <!-- VS Divider -->
                <div class="flex items-center gap-4 py-3 my-2">
                  <div class="flex-grow h-px bg-slate-200 dark:bg-slate-800 daylight:bg-day-ink/10"></div>
                  <span class="text-[10px] font-black text-slate-300 dark:text-slate-700 uppercase tracking-tighter">VS</span>
                  <div class="flex-grow h-px bg-slate-200 dark:bg-slate-800 daylight:bg-day-ink/10"></div>
                </div>

                <!-- Team B Row -->
                <div class="grid grid-cols-12 gap-4 items-center group">
                  <div class="col-span-6 flex items-center gap-3">
                    <div class="w-1.5 h-10 bg-accent rounded-full flex-shrink-0"></div>
                    <div>
                      <p class="text-[10px] font-bold text-accent uppercase tracking-wider">Team B</p>
                      <p class="font-bold text-lg text-slate-900 dark:text-white">${o.team2Name}</p>
                    </div>
                  </div>

                  <!-- Set 1 -->
                  <div class="col-span-2">
                    <input
                      type="number"
                      class="w-full h-14 bg-slate-100 dark:bg-slate-800/50 daylight:bg-day-sunken daylight:text-day-ink border-2 border-transparent focus:border-primary focus:ring-0 rounded-xl text-center text-2xl font-black transition-all set-input team2-set1"
                      name="set-1-team2"
                      value="${o.s1t2}"
                      placeholder="0"
                      min="0"
                      max="25"
                      required
                      tabindex="2"
                    />
                  </div>

                  <!-- Set 2 -->
                  <div class="col-span-2">
                    <input
                      type="number"
                      class="w-full h-14 bg-slate-100 dark:bg-slate-800/50 daylight:bg-day-sunken daylight:text-day-ink border-2 border-transparent focus:border-primary focus:ring-0 rounded-xl text-center text-2xl font-black transition-all set-input team2-set2"
                      name="set-2-team2"
                      value="${o.s2t2}"
                      placeholder="0"
                      min="0"
                      max="25"
                      required
                      tabindex="4"
                    />
                  </div>

                  <!-- Set 3 -->
                  <div id="set-3-wrapper-team2" class="col-span-2 opacity-40 transition-opacity">
                    <input
                      type="number"
                      class="w-full h-14 bg-slate-100 dark:bg-slate-800/50 daylight:bg-day-sunken daylight:text-day-ink border-2 border-transparent focus:border-primary focus:ring-0 rounded-xl text-center text-2xl font-black transition-all set-input team2-set3"
                      name="set-3-team2"
                      value="${o.s3t2}"
                      placeholder="0"
                      min="0"
                      max="25"
                      tabindex="6"
                      disabled
                    />
                  </div>
                </div>
              </div>

              <!-- Match Summary Card -->
              <div class="mt-10 p-6 bg-slate-50 dark:bg-slate-900/50 daylight:bg-day-sunken rounded-xl border border-dashed border-slate-200 dark:border-slate-800 daylight:border-day-ink/10">
                <div class="flex justify-between items-center">
                  <div class="text-center">
                    <p class="text-[10px] font-black text-slate-400 daylight:text-day-ink-muted uppercase mb-2">Total Sets A</p>
                    <p class="text-4xl font-black text-primary daylight:text-day-ink" id="team1-total">0</p>
                  </div>
                  <div class="h-12 w-px bg-slate-200 dark:bg-slate-800 daylight:bg-day-ink/10"></div>
                  <div class="text-center">
                    <span class="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-500 uppercase">
                      <span class="material-symbols-outlined text-sm mr-1">info</span>
                      Match Summary
                    </span>
                  </div>
                  <div class="h-12 w-px bg-slate-200 dark:bg-slate-800 daylight:bg-day-ink/10"></div>
                  <div class="text-center">
                    <p class="text-[10px] font-black text-slate-400 daylight:text-day-ink-muted uppercase mb-2">Total Sets B</p>
                    <p class="text-4xl font-black text-accent" id="team2-total">0</p>
                  </div>
                </div>
              </div>

              <!-- Validation Error Message -->
              <div id="validation-error" class="hidden p-3 mx-auto max-w-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm font-bold text-center rounded-lg border border-red-200 dark:border-red-800 transition-all"></div>

              <!-- Form Actions -->
              <div class="flex gap-4 pt-6 border-t border-slate-200 dark:border-slate-800 daylight:border-day-ink/10">
                <button
                  type="button"
                  id="cancel-btn"
                  class="flex-1 py-3 px-4 rounded-xl border-2 border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 text-slate-600 dark:text-slate-300 daylight:text-day-ink-soft font-bold hover:bg-slate-100 dark:hover:bg-slate-800 daylight:hover:bg-day-sunken transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  class="flex-[2] py-3 px-4 bg-primary text-navy-800 rounded-xl font-bold shadow-lg shadow-primary/20 daylight:shadow-day-ball flex items-center justify-center gap-2 hover:brightness-110 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:shadow-none disabled:active:scale-100"
                  id="submit-btn"
                >
                  <span class="material-symbols-outlined text-xl">check_circle</span>
                  Submit Match Result
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    `;
